import {
  Edit3,
  EyeOff,
  MapPinPlus,
  Plus,
  Save,
  Tag,
  Trash2,
  Undo2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  ViewportContextMenu,
  ViewportContextMenuItem,
} from "@/components/ui/viewport-context-menu";
import { ViewportSelectionPanel } from "@/components/ui/viewport-selection-panel";
import {
  getDetectionMatchText,
  getOptionMatchText,
  normalizeTagMatchText,
  type DocumentTagOption,
  type TagDetectionClassification,
} from "@/viewport-tools/tags";

interface PanelDetection {
  rawText: string;
  normalizedText?: string;
  classification: TagDetectionClassification;
  archivedAt?: number;
}

interface NormalizedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DocumentTagEditorSelectionAction {
  label: string;
  disabled?: boolean;
  onSelect: () => void;
}

const NO_TAG_OPTION_VALUE = "__no_tag_option__";

/**
 * Inputs and callbacks for editing, creating, and retagging document detections.
 * Classification inputs are no longer part of this contract; drafts persist as
 * unknown and consumers supply draft text plus optional tag-option bindings.
 */
export interface DocumentTagEditorPanelProps<TDetection extends PanelDetection> {
  panelClassName?: string;
  shouldRenderPanel: boolean;
  showEditToggle: boolean;
  editMode: boolean;
  allowCreate: boolean;
  createMode: boolean;
  pendingId: string | null;
  createDraftRect: NormalizedRect | null;
  selectedDetection: TDetection | null;
  selectedDetectionCount: number;
  selectedActiveDetectionCount: number;
  selectedArchivedDetectionCount: number;
  selectedDeletableDetectionCount: number;
  selectedDetectionWasCreatedByUser: boolean;
  canSaveSelection: boolean;
  /** Editable raw detection text for the current selection or create draft. */
  draftText: string;
  /** Available tag options for single-selection and bulk retag flows. */
  tagOptions: DocumentTagOption[];
  /** Selected tag option for the current draft, or null for no option. */
  draftTagOptionId: string | null;
  /** Target tag option used when bulk-retagging multiple selections. */
  bulkTargetOptionId: string | null;
  showBulkRetagControls: boolean;
  onDraftTextChange: (value: string) => void;
  onDraftTagOptionChange: (value: string) => void;
  onBulkTargetOptionChange: (value: string) => void;
  onToggleBulkRetagControls: () => void;
  onRequestBulkRetag: () => void;
  onToggleEditMode: () => void;
  onToggleCreateMode: () => void;
  onSave: () => void;
  onHideSelected: () => void;
  onUnhideSelected: () => void;
  onDelete: () => void;
  onClearSelection: () => void;
  selectedDetectionAction?: DocumentTagEditorSelectionAction | null;
}

interface TagActionButtonsProps {
  hasSelection: boolean;
  hasTagOptions: boolean;
  isMultiSelection: boolean;
  createDraftRect: NormalizedRect | null;
  pendingId: string | null;
  canSaveSelection: boolean;
  selectedActiveDetectionCount: number;
  selectedArchivedDetectionCount: number;
  canDeleteSelection: boolean;
  selectedDetectionWasCreatedByUser: boolean;
  onSave: () => void;
  onHideSelected: () => void;
  onUnhideSelected: () => void;
  onDelete: () => void;
  onToggleBulkRetagControls: () => void;
  selectedDetectionAction?: DocumentTagEditorSelectionAction | null;
}

export function DocumentTagContextMenu({
  x,
  y,
  pending,
  onClose,
  visibilityLabel,
  onToggleVisibility,
  selectedDetectionAction,
}: {
  x: number;
  y: number;
  pending: boolean;
  onClose: () => void;
  visibilityLabel: "Hide" | "Unhide";
  onToggleVisibility: () => void;
  selectedDetectionAction?: DocumentTagEditorSelectionAction | null;
}) {
  return (
    // Escape, outside clicks, and item selection close the menu through `onClose`.
    <ViewportContextMenu x={x} y={y} data-document-tag-context-menu onClose={onClose}>
      {selectedDetectionAction ? (
        <ViewportContextMenuItem
          icon={<MapPinPlus className="size-3.5" />}
          disabled={pending || selectedDetectionAction.disabled}
          onSelect={selectedDetectionAction.onSelect}
        >
          {selectedDetectionAction.label}
        </ViewportContextMenuItem>
      ) : null}
      <ViewportContextMenuItem
        icon={<EyeOff className="size-3.5" />}
        disabled={pending}
        onSelect={onToggleVisibility}
      >
        {visibilityLabel}
      </ViewportContextMenuItem>
    </ViewportContextMenu>
  );
}

function TagActionButtons({
  hasSelection,
  hasTagOptions,
  isMultiSelection,
  createDraftRect,
  pendingId,
  canSaveSelection,
  selectedActiveDetectionCount,
  selectedArchivedDetectionCount,
  canDeleteSelection,
  selectedDetectionWasCreatedByUser,
  onSave,
  onHideSelected,
  onUnhideSelected,
  onDelete,
  onToggleBulkRetagControls,
  selectedDetectionAction,
}: TagActionButtonsProps) {
  const saveLabel = createDraftRect ? "Create" : isMultiSelection ? "Apply" : "Save";
  const shouldShowDelete = isMultiSelection ? canDeleteSelection : selectedDetectionWasCreatedByUser;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        type="button"
        size="compact"
        onClick={onSave}
        disabled={pendingId !== null || !canSaveSelection}
      >
        <Save className="size-3.5" />
        {saveLabel}
      </Button>
      {hasSelection && selectedActiveDetectionCount > 0 ? (
        <Button
          type="button"
          size="compact"
          variant="outline"
          onClick={onHideSelected}
          disabled={pendingId !== null}
        >
          <EyeOff className="size-3.5" />
          Hide
        </Button>
      ) : null}
      {hasSelection && selectedArchivedDetectionCount > 0 ? (
        <Button
          type="button"
          size="compact"
          variant="outline"
          onClick={onUnhideSelected}
          disabled={pendingId !== null}
        >
          <Undo2 className="size-3.5" />
          Unhide
        </Button>
      ) : null}
      {hasSelection && !isMultiSelection && selectedDetectionAction ? (
        <Button
          type="button"
          size="compact"
          variant="outline"
          onClick={selectedDetectionAction.onSelect}
          disabled={pendingId !== null || selectedDetectionAction.disabled}
        >
          <MapPinPlus className="size-3.5" />
          {selectedDetectionAction.label}
        </Button>
      ) : null}
      {hasSelection && shouldShowDelete ? (
        <Button
          type="button"
          size="compact"
          variant="destructive"
          onClick={onDelete}
          disabled={pendingId !== null}
        >
          <Trash2 className="size-3.5" />
          Delete
        </Button>
      ) : null}
      {hasSelection && hasTagOptions && !isMultiSelection ? (
        <Button
          type="button"
          size="compact"
          variant="outline"
          onClick={onToggleBulkRetagControls}
        >
          Change
        </Button>
      ) : null}
    </div>
  );
}

export function DocumentTagEditorPanel<TDetection extends PanelDetection>({
  panelClassName,
  shouldRenderPanel,
  showEditToggle,
  editMode,
  allowCreate,
  createMode,
  pendingId,
  createDraftRect,
  selectedDetection,
  selectedDetectionCount,
  selectedActiveDetectionCount,
  selectedArchivedDetectionCount,
  selectedDeletableDetectionCount,
  selectedDetectionWasCreatedByUser,
  canSaveSelection,
  draftText,
  tagOptions,
  draftTagOptionId,
  bulkTargetOptionId,
  showBulkRetagControls,
  onDraftTextChange,
  onDraftTagOptionChange,
  onBulkTargetOptionChange,
  onToggleBulkRetagControls,
  onRequestBulkRetag,
  onToggleEditMode,
  onToggleCreateMode,
  onSave,
  onHideSelected,
  onUnhideSelected,
  onDelete,
  onClearSelection,
  selectedDetectionAction,
}: DocumentTagEditorPanelProps<TDetection>) {
  const hasTagOptions = tagOptions.length > 0;
  const isMultiSelection = selectedDetectionCount > 1;
  const canDeleteSelection = selectedDetectionCount > 0 && selectedDeletableDetectionCount === selectedDetectionCount;
  const selectedMatchText = selectedDetection
    ? normalizeTagMatchText(getDetectionMatchText(selectedDetection))
    : "";
  const bulkTargetOptions = selectedDetection
    ? tagOptions.filter((option) => getOptionMatchText(option) !== selectedMatchText)
    : [];
  const contextTitle = isMultiSelection
    ? `${selectedDetectionCount} tags selected`
    : selectedDetection?.rawText ?? "Tag";

  return (
    <ViewportSelectionPanel
      data-document-tag-editor-ui
      title={contextTitle}
      subtitle="Tag menu"
      icon={<Tag className="size-4 text-muted-foreground" />}
      onClose={onClearSelection}
      closeLabel="Close tag editor"
      className={cn(panelClassName, !shouldRenderPanel && "hidden")}
      headerActions={
        <>
          {showEditToggle ? (
            <IconButton
              label="Edit tags"
              icon={Edit3}
              variant="outline"
              iconClassName="size-3.5"
              pressed={editMode}
              onClick={onToggleEditMode}
            />
          ) : null}
          {allowCreate ? (
            <IconButton
              label="Add tag"
              icon={Plus}
              variant="outline"
              iconClassName="size-3.5"
              pressed={createMode}
              disabled={!editMode}
              onClick={onToggleCreateMode}
            />
          ) : null}
        </>
      }
    >
      {createDraftRect || selectedDetection ? (
        <>
          {isMultiSelection ? (
            <div className="rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
              {selectedDetectionCount} tags selected. Modifier-click tags to add or remove them.
            </div>
          ) : null}
          <div className="space-y-1.5">
            {hasTagOptions ? (
              <Select
                value={draftTagOptionId ?? NO_TAG_OPTION_VALUE}
                onValueChange={(value) => {
                  if (value !== NO_TAG_OPTION_VALUE) {
                    onDraftTagOptionChange(value);
                  }
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TAG_OPTION_VALUE} disabled>
                    Select tag
                  </SelectItem>
                  {tagOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={draftText}
                onChange={(event) => onDraftTextChange(event.currentTarget.value)}
                placeholder="Tag text"
                className="h-9 text-sm"
              />
            )}
          </div>

          <TagActionButtons
            hasSelection={selectedDetection !== null}
            hasTagOptions={hasTagOptions}
            isMultiSelection={isMultiSelection}
            createDraftRect={createDraftRect}
            pendingId={pendingId}
            canSaveSelection={canSaveSelection}
            selectedActiveDetectionCount={selectedActiveDetectionCount}
            selectedArchivedDetectionCount={selectedArchivedDetectionCount}
            canDeleteSelection={canDeleteSelection}
            selectedDetectionWasCreatedByUser={selectedDetectionWasCreatedByUser}
            onSave={onSave}
            onHideSelected={onHideSelected}
            onUnhideSelected={onUnhideSelected}
            onDelete={onDelete}
            onToggleBulkRetagControls={onToggleBulkRetagControls}
            selectedDetectionAction={selectedDetectionAction}
          />

          {!selectedDetection ? (
            <div className="text-xs text-muted-foreground">
              Draw a box on the document, then choose the tag details and create it.
            </div>
          ) : null}
          {selectedDetection && hasTagOptions && !isMultiSelection ? (
            <div className="space-y-1.5">
              {showBulkRetagControls ? (
                <div className="space-y-1.5 rounded-md border border-border/70 p-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Change matching
                  </div>
                  <Select
                    value={bulkTargetOptionId ?? NO_TAG_OPTION_VALUE}
                    onValueChange={(value) => {
                      if (value !== NO_TAG_OPTION_VALUE) {
                        onBulkTargetOptionChange(value);
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Change to tag" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_TAG_OPTION_VALUE} disabled>
                        Change to tag
                      </SelectItem>
                      {bulkTargetOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="compact"
                    variant="outline"
                    disabled={!bulkTargetOptionId || pendingId !== null || bulkTargetOptions.length === 0}
                    onClick={onRequestBulkRetag}
                  >
                    Apply
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <div className="text-sm text-muted-foreground">
          {editMode
            ? "Select a tag to edit it, change all matching tags, or use Add Tag to draw a new one."
            : showEditToggle
              ? "Enable Edit Tags to add, duplicate, move, hide, or unhide tags."
              : "Use the Tags tool in the left rail to add, duplicate, move, hide, unhide, delete, or bulk-change tags."}
        </div>
      )}
    </ViewportSelectionPanel>
  );
}
